import { useCallback, useState } from 'react'
import { useDispatch } from 'react-redux'

/**
 * Custom React hook for dispatching Redux actions with loading state and error handling.
 *
 * @param {Object|Function} action - Redux action or action creator to dispatch
 * @param {Object} [options={}] - Optional configuration object
 * @param {Function} [options.onSuccess] - Callback function called when action succeeds.
 *   Receives response data as parameter.
 * @param {Function} [options.onError] - Callback function called when action fails.
 *   Receives error as parameter.
 * @returns {Object} Hook return value
 * @returns {boolean} returns.isLoading - Loading state indicating if action is in progress
 * @returns {Function} returns.triggerAction - Async function to trigger the action dispatch
 * @returns {*} returns.data - Data returned from successful action execution
 * @returns {Error|null} returns.error - Error object if action failed, null otherwise
 *
 * @example
 * const { isLoading, triggerAction, data, error } = useAwaitDispatchAction(
 *   fetchUserData,
 *   {
 *     onSuccess: (data) => console.log('User data loaded:', data),
 *     onError: (error) => console.error('Failed to load user:', error)
 *   }
 * );
 * 
 * see usage in OrdersListPage.js for refresh price button
 */
export const useAwaitDispatchAction = (action, { onSuccess, onError } = {}) => {
  const dispatch = useDispatch()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [data, setData] = useState(null)

  const triggerAction = useCallback(async () => {
    setIsLoading(true)
    try {
      const res = await dispatch(action).meta.action.payload
      setData(res?.data)
      onSuccess?.(res?.data)
    } catch (error) {
      setError(error)
      onError?.(error)
    } finally {
      setIsLoading(false)
    }
  }, [dispatch, action, onError, onSuccess])

  return { isLoading, triggerAction, data, error }
}
